import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import { rootUrl } from '../../../config/config';
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa';

const LIMIT = 100;

function formatDateTime(ts) {
    if (!ts) return '—';
    const d = new Date(ts);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
        + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

const WebhookLogs = () => {
    const { token } = useAuth();

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [endpoint, setEndpoint] = useState('');
    const [secretMatched, setSecretMatched] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchData = useCallback(async (newOffset = 0) => {
        setLoading(true);
        setError('');
        try {
            const authToken = token || localStorage.getItem('authToken');
            const params = new URLSearchParams({ limit: LIMIT, offset: newOffset });
            if (from) params.set('from', from);
            if (to) params.set('to', to);
            if (endpoint) params.set('endpoint', endpoint);
            if (secretMatched !== '') params.set('secret_matched', secretMatched);
            const res = await axios.get(`${rootUrl}/api/payments/c2b/webhook-logs?${params}`, {
                headers: { Authorization: `Bearer ${authToken}` },
            });
            setRows(res.data.data || []);
            setTotal(res.data.total || 0);
            setOffset(newOffset);
        } catch {
            setError('Failed to load webhook logs. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [from, to, endpoint, secretMatched, token]);

    useEffect(() => { fetchData(0); }, [fetchData]);

    const handleReset = () => {
        setFrom('');
        setTo('');
        setEndpoint('');
        setSecretMatched('');
    };

    return (
        <div className="min-h-screen bg-brand-gradient p-4">
            {/* Filters */}
            <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-xl shadow p-4 mb-4 flex flex-wrap items-end gap-3">
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
                    <input type="date" value={from} onChange={e => setFrom(e.target.value)}
                        className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#FF5733]" />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
                    <input type="date" value={to} onChange={e => setTo(e.target.value)}
                        className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#FF5733]" />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Endpoint</label>
                    <select value={endpoint} onChange={e => setEndpoint(e.target.value)}
                        className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#FF5733]">
                        <option value="">All</option>
                        <option value="validate">Validate</option>
                        <option value="confirm">Confirm</option>
                    </select>
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Result</label>
                    <select value={secretMatched} onChange={e => setSecretMatched(e.target.value)}
                        className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#FF5733]">
                        <option value="">All</option>
                        <option value="true">Accepted</option>
                        <option value="false">Rejected</option>
                    </select>
                </div>
                <button onClick={handleReset} className="text-sm text-[#FF5733] hover:text-[#e64520] font-medium pb-1">
                    Reset
                </button>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-red-700 text-sm">{error}</div>
            )}

            {/* Table */}
            <div className="bg-white rounded-xl shadow overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center">
                        <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-[#FF5733]"></div>
                    </div>
                ) : rows.length === 0 ? (
                    <div className="p-8 text-center text-gray-400 text-sm">No webhook calls logged yet.</div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="px-4 py-2 text-left font-semibold text-gray-700">Date &amp; Time</th>
                                        <th className="px-4 py-2 text-left font-semibold text-gray-700">Endpoint</th>
                                        <th className="px-4 py-2 text-left font-semibold text-gray-700">Result</th>
                                        <th className="px-4 py-2 text-left font-semibold text-gray-700">Trans ID</th>
                                        <th className="px-4 py-2 text-left font-semibold text-gray-700">IP Address</th>
                                        <th className="px-4 py-2 text-center font-semibold text-gray-700">Code</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map(row => (
                                        <tr key={row.id} className="border-b border-gray-100 hover:bg-gray-50">
                                            <td className="px-4 py-2 text-gray-700 whitespace-nowrap text-xs">{formatDateTime(row.input_date)}</td>
                                            <td className="px-4 py-2">
                                                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                                    row.endpoint === 'confirm' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                                                }`}>
                                                    {row.endpoint}
                                                </span>
                                            </td>
                                            <td className="px-4 py-2">
                                                {row.secret_matched
                                                    ? <span className="flex items-center gap-1 text-green-600 text-xs font-medium"><FaCheckCircle /> Accepted</span>
                                                    : <span className="flex items-center gap-1 text-red-500 text-xs font-medium"><FaTimesCircle /> Rejected</span>
                                                }
                                            </td>
                                            <td className="px-4 py-2 font-mono text-xs text-gray-700">{row.trans_id || '—'}</td>
                                            <td className="px-4 py-2 font-mono text-xs text-gray-500">{row.ip_address || '—'}</td>
                                            <td className="px-4 py-2 text-center">
                                                <span className={`px-2 py-0.5 text-xs font-mono rounded ${
                                                    row.result_code === 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'
                                                }`}>
                                                    {row.result_code}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between text-sm text-gray-600">
                            <span>{offset + 1}–{Math.min(offset + LIMIT, total)} of {total}</span>
                            <div className="flex gap-2">
                                <button onClick={() => fetchData(offset - LIMIT)} disabled={offset === 0}
                                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                                    Previous
                                </button>
                                <button onClick={() => fetchData(offset + LIMIT)} disabled={offset + LIMIT >= total}
                                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                                    Next
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
            </div>
        </div>
    );
};

export default WebhookLogs;
